import React from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { useLoader } from "@react-three/fiber";
import { useRef, useEffect } from "react";
import gsap from "gsap";
import state from "../../state";
import { useSnapshot } from "valtio";

const getInitialMaterial = () => new THREE.MeshPhongMaterial({
  color: new THREE.Color(0xf1f1f1),
  shininess: 10
});

const INITIAL_MTL = new THREE.MeshPhongMaterial({
  color: new THREE.Color(0xf1f1f1),
  shininess: 10
});

const INITIAL_MAP = [
  { childID: "back", mtl: getInitialMaterial() },
  { childID: "base", mtl: getInitialMaterial() },
  {
    childID: "cushions",
    mtl: new THREE.MeshPhongMaterial({
      color: new THREE.Color(parseInt("0x" + "7C6862")),
      shininess: 10
    })
  },
  { childID: "legs", mtl: getInitialMaterial() },
  { childID: "supports", mtl: getInitialMaterial() }
];

const initColor = (parent, type, mtl) => {
  parent.traverse((o) => {
    if (o.isMesh && o.name.includes(type)) {
      o.castShadow = true;
      o.receiveShadow = true;
      o.material = mtl;
      o.nameID = type;
    }
  });
};

const ChairMesh = ({ newMaterialOpt }) => {
  const { scene: theModel } = useLoader(GLTFLoader, "chair.gltf");
  const snap = useSnapshot(state);
  const chair = useRef(theModel);

  useEffect(() => {
    if (snap.hoveredType) {
      if (!snap.highlight[snap.hoveredType]) highlightMaterial(snap.hoveredType);
    }
  }, [snap.hoveredType]);

  useEffect(
    () => void setMaterial(newMaterialOpt.activeOption, newMaterialOpt.newMTL),
    [newMaterialOpt.newMTL]
  );

  useEffect(() => {
    if (theModel) {
      for (let object of INITIAL_MAP) {
        initColor(theModel, object.childID, object.mtl);
      }
    }
  }, [theModel]);

  const setMaterial = (type, mtl) => {
    if (!mtl) return;
    theModel.traverse((o) => {
      if (o.isMesh && o.nameID != null) {
        if (o.nameID === type) {
          if (mtl.map) {
            o.material = mtl;
            return;
          }
          const newMat = new THREE.MeshPhongMaterial({
            color: new THREE.Color(o.material.color.r, o.material.color.g, o.material.color.b),
            shininess: 10
          });
          o.material = newMat;
          gsap.to(o.material.color, {
            r: mtl.color.r,
            g: mtl.color.g,
            b: mtl.color.b,
            duration: 0.5, // Change duration as needed
            onUpdate: () => {
              o.material.needsUpdate = true;
            }
          });
        }
      }
    });
  };

  const highlightMaterial = (type) => {
    theModel.traverse((o) => {
      if (o.isMesh && o.nameID != null) {
        if (o.nameID === type) {
          const oldColor = {
            r: o.material.color.r,
            g: o.material.color.g,
            b: o.material.color.b
          };
          state.highlight[type] = true;
          gsap.to(o.material.color, {
            r: 1,
            g: 1,
            b: 0,
            duration: 0.5, // Change duration as needed
            onUpdate: () => {
              o.material.needsUpdate = true;
            }
          });
          setTimeout(() => {
            gsap.to(o.material.color, {
              r: oldColor.r,
              g: oldColor.g,
              b: oldColor.b,
              duration: 0.5, // Change duration as needed
              onUpdate: () => {
                o.material.needsUpdate = true;
              },
              onComplete: () => {
                state.highlight[type] = false;
              }
            });
          }, 500);
        }
      }
    });
  };

  return (
    <primitive
      ref={chair}
      object={theModel}
      scale={[2, 2, 2]}
      rotation={[0, Math.PI, 0]}
      position={[0, -1, 0]}
      receiveShadow
      castShadow
    ></primitive>
  );
};

export default ChairMesh;
